import React, { useEffect, useRef, forwardRef } from 'react';
import classnames from 'classnames';
import _throttle from 'lodash/throttle';
import * as echarts from 'echarts';
import useDeepCompareEffect from '@/lowcode/common/hooks/useDeepCompareEffect';
import { CnChartProps } from './types';
import themeJson from './theme.json';
import './index.scss';

const clsPrefix = 'cn-chart';

/**
 * CnChart 组件
 * @param {*} props
 * @returns
 */
const CnChart = forwardRef((props: CnChartProps, ref: any) => {
  const { width, height, className, containerStyle, ...rest } = props;
  const container = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!container.current) {
      return;
    }

    echarts.registerTheme('default-theme', themeJson);
    const chart = echarts.init(container.current, 'default-theme', {
      width,
      height,
    });
    chartRef.current = chart;

    if (ref) {
      ref.current = chart;
    }

    chartRef.current.setOption({ ...rest });

    if (ResizeObserver) {
      const ro = new ResizeObserver(
        _throttle((entries) => {
          if (entries?.length) {
            const rectWidth = entries[0]?.contentRect?.width;
            const rectHeight = entries[0]?.contentRect?.height;
            if (rectWidth && rectHeight) {
              chartRef.current?.resize({
                width: rectWidth,
                height: rectHeight,
                animation: {
                  duration: 200,
                  easing: 'cubicOut',
                },
              });
            }
          }
        }, 200),
      );
      ro.observe(container.current);
    }
  }, []);

  useDeepCompareEffect(() => {
    if (chartRef.current) {
      chartRef.current.setOption({ ...rest }, true);
    }
  }, [rest]);

  return (
    <div ref={container} className={classnames(clsPrefix, className)} style={containerStyle} />
  );
});

CnChart.displayName = 'CnChart';

export { CnChart, echarts };
